import React, { useEffect } from 'react';
import { Apple, Heart, Leaf, ArrowRight, BookOpen, Share2, Facebook, Twitter, Mail, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { BOOKING_URL } from '@/constants/urls';
import { WelcomeModal } from '@/components/WelcomeModal';
import BlogPostingSchema from '@/components/BlogPostingSchema';
import fertilityFoodsImage from '@/assets/blog/fertility-foods-to-eat.webp';

const FertilityFoodsBlog = () => {
  // Force scroll to top on page load (ignoring anchors)
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update document title and meta tags
    document.title = "Fertility Foods Currimundi | Hormone Health Guide";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Discover fertility-boosting foods and hormone health nutrition at our Currimundi Chinese Medicine clinic. Expert guidance on diet for reproductive wellness.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Fertility Foods: What to Eat for Hormone Health | Women and the Way");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Discover the best foods for fertility and hormone health. Learn what to eat to support your reproductive wellness journey.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/fertility-foods-hormone-health");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', fertilityFoodsImage);
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Fertility Foods: What to Eat for Hormone Health | Women and the Way");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = "Fertility Foods to Nourish Hormone Health | Women and the Way";
    const text = "Discover the top foods that support fertility and hormone balance. Learn what to eat and what to avoid when preparing for pregnancy - naturally and holistically.";
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        const subject = "Fertility Foods to Nourish Hormone Health";
        const body = `${text}\n\nRead more: ${url}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        break;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <>
      <BlogPostingSchema
        title="Fertility Foods: What to Eat for Hormone Health"
        description="Discover the best foods for fertility and hormone health. Learn what to eat to support your reproductive wellness journey."
        image={fertilityFoodsImage}
        url="https://womenandtheway.com.au/blog/fertility-foods-hormone-health"
        publishedDate="2025-01-08"
        modifiedDate="2025-01-27"
        keywords="fertility foods, hormone health, reproductive nutrition, women's health, Sunshine Coast, fertility clinic, Chinese medicine, preconception diet"
        articleSection="Fertility & Women's Health"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section with Image */}
        <section className="relative py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="w-full md:w-1/2 mb-10 md:mb-0">
                <div className="mb-8">
                  <span className="text-5xl md:text-6xl mb-4 block animate-float">🌿</span>
                  <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    Fertility Foods to Nourish Hormone Health
                  </h1>
                  <p className="text-xl text-gray-600 font-light">
                    What to Eat (and What to Avoid) When Preparing for Pregnancy
                  </p>
                </div>
              </div>
              <div className="w-full md:w-5/12 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
                  <img 
                    src={fertilityFoodsImage} 
                    alt="Fertility foods and hormone health at Women and the Way, Sunshine Coast – colorful fruits, vegetables, and healthy ingredients for pregnancy preparation" 
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-amber-200/50 w-3/4 md:w-1/2">
                  <p className="text-sage-800 font-serif font-medium">
                    "Nourish your body, nourish your fertility."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            {/* Introduction */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🧡</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Introduction</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    What you eat plays a powerful role in your fertility journey. The right foods can help balance hormones, reduce inflammation, and support the health of your eggs, uterus, and future baby.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700">
                    Whether you're just beginning to think about conceiving or actively preparing your body, food is one of the most natural ways to support your reproductive health.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Fertility-Boosting Foods */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🥗</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Fertility-Boosting Foods to Add to Your Plate</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    These nutrient-dense foods are rich in antioxidants, healthy fats, and key fertility nutrients like folate, zinc, and omega-3s. Try incorporating a variety of them throughout your cycle:
                  </p>
                  <div className="space-y-4">
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Berries</h4>
                      <p className="text-gray-600">Packed with antioxidants that protect eggs and reduce oxidative stress</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Leafy Greens (like spinach & kale)</h4>
                      <p className="text-gray-600">High in iron and folate to support ovulation</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Avocados</h4>
                      <p className="text-gray-600">A rich source of vitamin E and healthy monounsaturated fats for hormone production</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Citrus Fruits</h4>
                      <p className="text-gray-600">High in vitamin C to support progesterone levels and immune health</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Legumes (lentils, chickpeas)</h4>
                      <p className="text-gray-600">Full of plant-based protein and fiber to regulate insulin</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Nuts & Seeds</h4>
                      <p className="text-gray-600">Provide omega-3s, selenium, and vitamin E for hormone and egg health</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Whole Grains (quinoa, oats, brown rice)</h4>
                      <p className="text-gray-600">Help stabilize blood sugar and support adrenal function</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Cruciferous Vegetables (broccoli, cauliflower, Brussels sprouts)</h4>
                      <p className="text-gray-600">Aid liver detox pathways and estrogen balance</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Salmon & Fatty Fish</h4>
                      <p className="text-gray-600">Rich in omega-3s, crucial for reducing inflammation and hormone regulation</p>
                    </div>
                    <div className="border-l-4 border-sage-green pl-4">
                      <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Eggs</h4>
                      <p className="text-gray-600">Nutrient powerhouses loaded with choline, vitamin D, and protein for egg quality</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-soft-pink rounded-lg border-2 border-amber-200/50">
                    <p className="text-gray-700 font-medium">
                      🌸 Focus on real, whole foods that nourish your body at a cellular level.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Foods to Avoid */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🚫</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Foods to Limit or Avoid for Hormonal Harmony</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Certain foods can disrupt hormone function, spike insulin, or increase inflammation - making it harder for your body to find its natural rhythm.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Highly processed foods</h4>
                        <p className="text-gray-600">Often contain additives and preservatives that stress the liver</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Refined sugar & soft drinks</h4>
                        <p className="text-gray-600">Can lead to insulin resistance and inflammation</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Fried foods & trans fats</h4>
                        <p className="text-gray-600">May interfere with ovulation and egg quality</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">High-GI carbohydrates</h4>
                        <p className="text-gray-600">Cause blood sugar spikes that throw off hormone balance</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Red & processed meats</h4>
                        <p className="text-gray-600">Linked to higher levels of inflammation and oxidative stress</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Sugary dairy products</h4>
                        <p className="text-gray-600">Choose organic or unsweetened options like plain yogurt</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Alcohol & excessive caffeine</h4>
                        <p className="text-gray-600">Can deplete nutrients and interfere with ovulation</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-red-500 mt-1 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold mb-1 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Artificial sweeteners</h4>
                        <p className="text-gray-600">May disrupt gut health and hormone signaling pathways</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-sage-green/10 rounded-lg border-2 border-amber-200/50">
                    <p className="text-gray-700 font-medium">
                      🌿 Aim for balance, not restriction. Your body thrives on consistency, nourishment, and variety.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Takeaway */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">💛</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Takeaway: Eat with Intention</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    When preparing your body for pregnancy, food is one of the most empowering tools you can use. Prioritizing colorful, nutrient-dense meals rich in whole foods helps regulate your cycle, support egg health, and create a fertile foundation.
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700 font-medium">
                    Your hormones - and future baby - will thank you.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Share Component */}
            <div className="my-8 text-center">
              <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-amber-200/50">
                <h3 className="font-serif text-xl font-semibold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Share this article</h3>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>Twitter/X</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('email')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Link</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <section className="text-center py-16">
              <div className="bg-sage-800 rounded-2xl p-12 shadow-lg border-2 border-amber-200/50 max-w-2xl mx-auto">
                <h2 className="font-serif text-3xl font-bold text-white mb-6">Ready to nourish your fertility naturally?</h2>
                <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
                  Our team at Women and the Way offers personalized fertility support, including nutrition, acupuncture, herbal medicine, and lifestyle guidance.
                </p>
                <Button 
                  size="lg" 
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                  onClick={() => window.open(BOOKING_URL, '_blank')}
                >
                  Book Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-sm text-sage-200 mt-4">Start your fertility journey today</p>
              </div>
            </section>
          </div>
        </main>
      </div>
              {/* Footer removed - App.tsx handles global footer */}
      <WelcomeModal />
    </>
  );
};

export default FertilityFoodsBlog; 